import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft, Phone, MapPin, Truck, Users, Clock, FileText, Image, CheckSquare, AlertOctagon, Tool,
  CheckCircle, XCircle, RefreshCw, Printer, ExternalLink, List,
} from "react-feather";
import SectionCard from "../../../components/common/SectionCard";
import CompactInfoBlock, { CompactInfoField } from "../../../components/common/CompactInfoBlock";
import StatusBadge from "../../../components/common/StatusBadge";
import AlarmActionModal from "../components/AlarmActionModal";
import AlarmPhotoGallery from "../components/AlarmPhotoGallery";
import AlarmTimeline from "../components/AlarmTimeline";
import CauseChip from "../components/CauseChip";
import AlarmSheetPrintButton from "../components/AlarmSheetPrintButton";
import { useGetAlarmResponseQuery } from "../alarmsApi";
import {
  ALARM_STATUS_CLASS, ALARM_STATUS_LABEL, ARRIVAL_WARN_METERS, INCIDENT_LABEL,
  formatAccuracy, formatDateTime, formatDistance, formatDuration, isLowAccuracy, mapsAddressLink, mapsLink,
  useCanDispatchAlarms,
} from "../alarmFormat";
import { paths } from "../../../routes/paths";

const MapLink = ({ lat, lng, accuracy, label = "Map" }) => {
  const href = mapsLink(lat, lng);
  if (!href) return <span className="text-muted">No GPS</span>;
  const low = isLowAccuracy(accuracy);
  return (
    <>
      <a href={href} target="_blank" rel="noopener noreferrer" className="d-inline-flex align-items-center gap-1">
        <MapPin size={13} /> {label}
      </a>
      {accuracy != null && (
        <span
          className={`badge ms-1 ${low ? "badge-linewarning" : "badge-linesecondary"}`}
          title={low ? "Not a GPS fix - the position may be far off" : "Reported GPS accuracy"}
        >
          {low ? `Low GPS ${formatAccuracy(accuracy)}` : formatAccuracy(accuracy)}
        </span>
      )}
    </>
  );
};

// One row of the step table: when, who, where.
const StepRow = ({ label, date, by, children }) => (
  <tr>
    <td className="fw-semibold text-nowrap">{label}</td>
    <td className="text-nowrap">{date ? formatDateTime(date) : <span className="text-muted">Not yet</span>}</td>
    <td>{by || ""}</td>
    <td>{children}</td>
  </tr>
);

// The control room's view of one alarm. Every action button mirrors the proc
// that enforces it (Batch177), so nothing is offered that the server refuses:
//   Reassign  DISPATCHED only        Cancel  DISPATCHED / OPENED
//   Close / Push to Tech  REPORTED   Cause   REPORTED / CLOSED / CONVERTED
const AlarmResponseDetailPage = () => {
  const { recordGuid } = useParams();
  const canDispatch = useCanDispatchAlarms();
  const [mode, setMode] = useState(null);
  const { data, isLoading, error } = useGetAlarmResponseQuery(recordGuid, { pollingInterval: 30000 });

  if (isLoading) return <div className="page-wrapper"><div className="content">Loading...</div></div>;
  if (error || !data) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="alert alert-danger">{error?.message || "Response not found."}</div>
        </div>
      </div>
    );
  }

  const r = data.response;
  const reported = r.status === "REPORTED";
  const causeAllowed = ["REPORTED", "CLOSED", "CONVERTED"].includes(r.status);
  const arrivalLowGps = isLowAccuracy(r.arrivedAccuracyMeters);
  // A distance from a low-accuracy position is not evidence, so it is never shown as a red flag.
  const arrivalFar =
    !arrivalLowGps && r.arrivalToCustomerMeters != null && r.arrivalToCustomerMeters > ARRIVAL_WARN_METERS;
  const customerMap = mapsLink(r.customerLatitude, r.customerLongitude) || mapsAddressLink(r.siteAddress);

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          <Link to={paths.alarmBoard} className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1">
            <ArrowLeft size={14} /> Board
          </Link>
          <h4 className="mb-0 ms-1">{r.responseNumber}</h4>
          <StatusBadge status={r.status} classMap={ALARM_STATUS_CLASS} labelMap={ALARM_STATUS_LABEL} />
          <CauseChip cause={r.alarmCause} size={13} />
          <div className="ms-auto d-flex flex-wrap gap-2">
            {canDispatch && r.status === "DISPATCHED" && (
              <button type="button" className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1" onClick={() => setMode("reassign")}>
                <RefreshCw size={14} /> Reassign
              </button>
            )}
            {canDispatch && ["DISPATCHED", "OPENED"].includes(r.status) && (
              <button type="button" className="btn btn-sm btn-outline-danger d-inline-flex align-items-center gap-1" onClick={() => setMode("cancel")}>
                <XCircle size={14} /> Cancel
              </button>
            )}
            {canDispatch && causeAllowed && (
              <button type="button" className="btn btn-sm btn-outline-danger d-inline-flex align-items-center gap-1" onClick={() => setMode("cause")}>
                <AlertOctagon size={14} /> {r.alarmCause ? "Change cause" : "Set cause"}
              </button>
            )}
            {canDispatch && reported && (
              <>
                <button type="button" className="btn btn-sm btn-primary d-inline-flex align-items-center gap-1" onClick={() => setMode("push")}>
                  <Tool size={14} /> Push to Tech
                </button>
                <button type="button" className="btn btn-sm btn-success d-inline-flex align-items-center gap-1" onClick={() => setMode("close")}>
                  <CheckCircle size={14} /> Close
                </button>
              </>
            )}
            <AlarmSheetPrintButton detail={data} icon={Printer} />
          </div>
        </div>

        {reported && canDispatch && (
          <div className="alert alert-warning py-2">
            {r.unitName} reported back {formatDateTime(r.reportedDate)}. Review the report, then set the cause and close it
            or push it to Tech.
          </div>
        )}

        <div className="row">
          <div className="col-lg-7">
            <SectionCard icon={AlertOctagon} title={`${INCIDENT_LABEL[r.incidentType] || r.incidentType} alarm`}>
              <div style={{ whiteSpace: "pre-wrap" }} className="mb-2">{r.incidentDescription}</div>
              <CompactInfoBlock>
                <CompactInfoField icon={Clock} label="Signal" value={formatDateTime(r.signalReceivedAt)} />
                <CompactInfoField label="Branch" value={r.branchCode} />
                <CompactInfoField label="Dispatched by" value={r.dispatchedByName} />
              </CompactInfoBlock>
            </SectionCard>

            <SectionCard icon={MapPin} title={r.customerName}
              right={customerMap && (
                <a href={customerMap} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1">
                  <ExternalLink size={13} /> Open in Maps
                </a>
              )}
            >
              <CompactInfoBlock>
                <CompactInfoField icon={Phone} label="Phone" value={r.customerPhone} />
                <CompactInfoField label="Site" value={r.siteLabel} />
                <CompactInfoField label="Pin" value={r.customerLatitude != null ? `${r.customerLatitude}, ${r.customerLongitude}` : "Not saved"} />
              </CompactInfoBlock>
              {r.siteAddress && <div className="text-muted small">{r.siteAddress}</div>}
            </SectionCard>

            <SectionCard icon={Clock} title="Response times">
              <div className="table-responsive">
                <table className="table table-sm align-middle mb-2">
                  <thead>
                    <tr><th>Step</th><th>Time</th><th>By</th><th>Location</th></tr>
                  </thead>
                  <tbody>
                    <StepRow label="Signal" date={r.signalReceivedAt} />
                    <StepRow label="Dispatched" date={r.dispatchedDate} by={r.dispatchedByName}>To {r.unitName}</StepRow>
                    <StepRow label="Opened (start)" date={r.openedDate} by={r.openedByName}>
                      {r.openedDate && (
                        <>
                          <MapLink lat={r.openedLatitude} lng={r.openedLongitude} accuracy={r.openedAccuracyMeters} label="Start point" />
                          {r.startToCustomerMeters != null && (
                            <span className="text-muted small">
                              {" "}· {isLowAccuracy(r.openedAccuracyMeters) ? "~" : ""}{formatDistance(r.startToCustomerMeters)} from site
                            </span>
                          )}
                        </>
                      )}
                    </StepRow>
                    <StepRow label="Arrived" date={r.arrivedDate} by={r.arrivedByName}>
                      {r.arrivedDate && (
                        <>
                          <MapLink lat={r.arrivedLatitude} lng={r.arrivedLongitude} accuracy={r.arrivedAccuracyMeters} label="Arrival point" />
                          {r.arrivalToCustomerMeters != null && (
                            <span className={`small ${arrivalFar ? "text-danger fw-semibold" : "text-muted"}`}>
                              {" "}· {arrivalLowGps ? "~" : ""}{formatDistance(r.arrivalToCustomerMeters)} from the customer&apos;s pin
                            </span>
                          )}
                          {r.arrivedQrVerified ? (
                            <span className="badge badge-linesuccess ms-2">QR scanned</span>
                          ) : (
                            <span className="badge badge-linesecondary ms-2">No QR</span>
                          )}
                        </>
                      )}
                    </StepRow>
                    <StepRow label="Reported" date={r.reportedDate} by={r.reportedByName}>
                      {r.reportedDate && (
                        <MapLink lat={r.reportedLatitude} lng={r.reportedLongitude} accuracy={r.reportedAccuracyMeters} label="Report point" />
                      )}
                    </StepRow>
                  </tbody>
                </table>
              </div>
              <CompactInfoBlock>
                <CompactInfoField label="Response" value={formatDuration(r.responseSeconds)} />
                <CompactInfoField label="Travel" value={formatDuration(r.travelSeconds)} />
                <CompactInfoField label="On site" value={formatDuration(r.onSiteSeconds)} />
                <CompactInfoField label="Total" value={formatDuration(r.totalSeconds)} />
              </CompactInfoBlock>
              {arrivalLowGps && (
                <div className="alert alert-warning py-2 mt-2 mb-0 small">
                  The arrival position is only accurate to {formatAccuracy(r.arrivedAccuracyMeters)} - it was not a phone GPS fix
                  (for example, marked from a computer browser). The distance from the customer cannot be relied on for this alarm.
                </div>
              )}
              {arrivalFar && (
                <div className="alert alert-danger py-2 mt-2 mb-0 small">
                  Arrival was marked {formatDistance(r.arrivalToCustomerMeters)} from the customer&apos;s saved pin. Either the pin is
                  wrong or the unit was not at the site.
                </div>
              )}
            </SectionCard>

            <SectionCard icon={FileText} title="Report from the unit">
              {r.reportedDate ? (
                <>
                  <div style={{ whiteSpace: "pre-wrap" }} className="mb-3">{r.unitRemarks}</div>
                  <h6 className="d-flex align-items-center gap-1 small fw-bold"><CheckSquare size={14} /> Checklist</h6>
                  {data.checklist.length === 0 ? (
                    <p className="text-muted small">No checklist items were filled in.</p>
                  ) : (
                    <table className="table table-sm mb-0">
                      <tbody>
                        {data.checklist.map((c) => (
                          <tr key={c.checklistItemId}>
                            <td style={{ width: 28 }}>{c.isChecked ? <CheckCircle size={16} color="#1E7B4A" /> : <XCircle size={16} color="#ADB5BD" />}</td>
                            <td className="fw-semibold">{c.itemName}</td>
                            <td>{c.remarks || <span className="text-muted">—</span>}</td>
                            <td className="text-muted small text-nowrap">{c.deviceCheckedAt ? formatDateTime(c.deviceCheckedAt) : ""}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </>
              ) : (
                <p className="text-muted small mb-0">The unit has not reported back yet.</p>
              )}
            </SectionCard>

            <SectionCard icon={Image} title={`Photos (${data.photos.length})`}>
              <AlarmPhotoGallery recordGuid={r.recordGuid} photos={data.photos} />
            </SectionCard>
          </div>

          <div className="col-lg-5">
            {/* The vehicle is the unit master's linked vehicle (Mobile Units screen). It
                is named in the title and again as a field; when no vehicle is linked the
                panel says so rather than hiding it - a blank looked like a missing
                feature (2026-09-22) when it was really an unlinked unit. */}
            <SectionCard icon={Truck} title={r.vehicleRegNo ? `${r.unitName} · ${r.vehicleRegNo}` : r.unitName}>
              <CompactInfoBlock>
                {r.vehicleRegNo ? (
                  <CompactInfoField label="Vehicle" value={r.vehicleRegNo} />
                ) : (
                  <span className="d-inline-flex align-items-center mb-1" style={{ fontSize: 13 }}>
                    <span className="text-muted me-1">Vehicle</span>
                    <span className="fst-italic text-muted">not linked to this unit</span>
                    <Link to={paths.alarmMobileUnits} className="ms-2 small">Mobile Units</Link>
                  </span>
                )}
              </CompactInfoBlock>
              <h6 className="d-flex align-items-center gap-1 small fw-bold mt-2"><Users size={14} /> Crew</h6>
              {data.crew.length === 0 ? (
                <p className="text-muted small mb-0">Recorded when the unit opens the alarm.</p>
              ) : (
                <ul className="mb-0 ps-3">
                  {data.crew.map((c) => (
                    <li key={c.employeeRecordGuid}>
                      {[c.empName, c.empLastName].filter(Boolean).join(" ")} <span className="text-muted">({c.empCode})</span>
                    </li>
                  ))}
                </ul>
              )}
            </SectionCard>

            <SectionCard icon={CheckCircle} title="Control room outcome">
              {r.alarmCause && (
                <div className="mb-2">
                  <CauseChip cause={r.alarmCause} />
                  {r.causeRemarks && <span className="ms-2">{r.causeRemarks}</span>}
                  <div className="text-muted small">
                    {r.causeMarkedByName} · {formatDateTime(r.causeMarkedDate)}
                  </div>
                </div>
              )}
              {r.status === "CONVERTED" && (
                <div className="mb-2">
                  <div className="fw-semibold">
                    Pushed to Tech:{" "}
                    {r.technicalWorkRecordGuid ? (
                      <Link to={paths.technicalWorkDetail(r.technicalWorkRecordGuid)}>{r.technicalWorkNumber}</Link>
                    ) : (
                      r.technicalWorkNumber
                    )}
                    {r.technicalWorkStatus && <span className="badge badge-lineinfo ms-2">{r.technicalWorkStatus}</span>}
                  </div>
                  <div className="text-muted small">{r.convertedByName} · {formatDateTime(r.convertedDate)}</div>
                </div>
              )}
              {r.status === "CLOSED" && (
                <div className="text-muted small mb-2">Closed by {r.closedByName} · {formatDateTime(r.closedDate)}</div>
              )}
              {r.actionTaken && (
                <div className="mb-2">
                  <div className="small fw-bold">Action taken</div>
                  <div style={{ whiteSpace: "pre-wrap" }}>{r.actionTaken}</div>
                </div>
              )}
              {r.status === "CANCELLED" && (
                <div>
                  <div className="fw-semibold text-danger">Cancelled</div>
                  <div>{r.cancelReason}</div>
                  <div className="text-muted small">{r.cancelledByName} · {formatDateTime(r.cancelledDate)}</div>
                </div>
              )}
              {!r.alarmCause && !r.actionTaken && !["CONVERTED", "CLOSED", "CANCELLED"].includes(r.status) && (
                <p className="text-muted small mb-0">Nothing decided yet.</p>
              )}
            </SectionCard>

            <SectionCard icon={List} title="History">
              <AlarmTimeline activity={data.activity} />
            </SectionCard>
          </div>
        </div>

        <AlarmActionModal mode={mode} response={r} onClose={() => setMode(null)} />
      </div>
    </div>
  );
};

export default AlarmResponseDetailPage;
